import * as z from 'zod';
import { emailSchema, isoDateTimeSchema, machineKeySchema, strict, uuidSchema } from './common.js';
import { CORE_FIELD_KEYS, IMAGES_COMPLETENESS_KEY } from './field.js';
/**
 * The administrator-editable settings, as one registry.
 *
 * A registry rather than a schema per call site, because these values are read from a `settings`
 * table as JSON and a value that was never validated is a value that fails somewhere far away from
 * where it was typed. Every key names its schema, its shipped default, and whether it is a secret
 * that must be stored encrypted.
 *
 * **The three product caps are deliberately NOT here.** Five images, ten ad-hoc fields and five
 * thousand characters of notes are product constants enforced in three layers, and a settings row
 * cannot reach a CHECK constraint or a trigger. Raising such a setting would produce a raw
 * constraint violation on the sixth upload; lowering it would do nothing at all. They live in
 * `limits.ts`.
 *
 * **Retention ships disabled and unset**, which is the owner's decision of record: the job, its
 * warning window and its report all exist and are tested, and nothing is ever destroyed until an
 * administrator deliberately turns it on and names a period.
 */
/**
 * A five-field cron expression.
 *
 * Validated by splitting rather than by one large pattern: a single expression covering all five
 * fields is the shape that backtracks catastrophically, and the value here comes from a form.
 */
const cronSchema = z
    .string()
    .max(120)
    .refine((value) => {
    const parts = value.trim().split(/\s+/u);
    return parts.length === 5 && parts.every((part) => /^[\d*,/-]{1,32}$/u.test(part));
}, { error: 'errors.validation.invalid_format' });
/**
 * What a registration carrying no band produces, as a schema two packages share.
 *
 * Named and exported rather than written inline in the registry, because the client bootstrap
 * (`system.ts`) carries the same three values and the browser turns them into named booleans: one
 * declaration, so a value added here cannot be missing there.
 */
export const noBandAtRegistrationSchema = z.enum(['refuse', 'defer', 'emergency']);
/** One to five addresses, and never more: a mailing list belongs on the mail server. */
const recipientsSchema = z.array(emailSchema).max(5);
/** The keys the completeness warning may name: a real field, or the images token. */
const completenessKeySchema = z.union([machineKeySchema, z.literal(IMAGES_COMPLETENESS_KEY)]);
/**
 * Every setting, with its shipped default.
 *
 * The defaults here are the ones a fresh installation boots with, and they are the same numbers the
 * operator manual documents. A `db` test compares this registry against the seeded rows.
 */
export const SETTINGS_REGISTRY = {
    /**
     * An override needs a reason code, and this value can only ever be `true`.
     *
     * `z.literal(true)` rather than `z.boolean()`, because `false` is unrepresentable in the engine:
     * `presence_events_override_reason_ck` requires `reason_code` on every `override` and
     * `blocked_override` unconditionally, and AD-31 states the rule unconditionally. Writing `false`
     * would therefore loosen nothing and would surface as a raw constraint violation at a gate with
     * somebody standing at it, which is exactly the failure this file's opening note describes for
     * the three product caps that are deliberately not settings at all. Substituting a default reason
     * instead is worse: it would write a fact nobody stated into the one table that admits no
     * correction, and Section 1.12's Integrity panel counts overrides BY reason.
     *
     * The key is kept rather than deleted so the documented setting still exists and still reads as a
     * guarantee; what is refused is the value that cannot be honoured. The presence service takes no
     * settings dependency for this at all: the requirement is a constant of the state machine.
     */
    'presence.require_override_reason': {
        schema: z.literal(true),
        defaultValue: true,
        secret: false,
    },
    /**
     * The allowance: how long somebody may be outside before the sweep says they are not back.
     *
     * **Minutes, not hours, because this facility is a residence.** People live inside and go out for
     * a while; the condition worth an alert is a return that has not happened, and an hour is the
     * order of magnitude an excursion is measured in. Its predecessor measured the other side —
     * how long somebody had been INSIDE — which is a question a residence never asks.
     *
     * **The floor is five because the sweep runs every five minutes.** A threshold finer than the
     * cadence that evaluates it is a number this product cannot honour, and a setting that silently
     * means something coarser than it says is worse than one that refuses the value. The ceiling is a
     * day: past twenty-four hours outside, an installation that did not want to be told has a policy
     * problem rather than a settings problem.
     *
     * **There is deliberately no enable flag.** The alert is the whole of what replaced the nightly
     * sweep that used to write movements on a timer, and a safety monitor with an off switch is a
     * safety monitor that is off. An installation that wants it quieter raises the allowance.
     */
    'presence.outside_too_long_minutes': {
        schema: z.int().min(5).max(1440),
        defaultValue: 60,
        secret: false,
    },
    /**
     * How long a repeat read of the same code is discarded for, in the browser (AD-30).
     *
     * Fifteen hundred rather than four hundred, on sourced grounds: a reader's "reread delay", which
     * is how long before it will read the same symbol again, defaults to 750 ms on Honeywell hardware
     * and is selectable at 500, 750, 1000 and 2000. At four hundred, every re-read of a band left in
     * front of a presentation-mode scanner fell outside the window and was submitted as a fresh scan.
     */
    'scan.client_burst_dedupe_ms': {
        schema: z.int().min(0).max(5000),
        defaultValue: 1500,
        secret: false,
    },
    'session.idle_minutes_gate': { schema: z.int().min(1).max(120), defaultValue: 5, secret: false },
    'session.idle_minutes_admin': {
        schema: z.int().min(1).max(480),
        defaultValue: 60,
        secret: false,
    },
    'session.absolute_hours': { schema: z.int().min(1).max(72), defaultValue: 12, secret: false },
    'security.totp_required_admins': { schema: z.boolean(), defaultValue: true, secret: false },
    'security.totp_required_operators': { schema: z.boolean(), defaultValue: false, secret: false },
    /**
     * Section 1.7's self-service reset, as a switch an administrator holds.
     *
     * It ships ON, because the capability is already gated by whether this installation has a mail
     * server at all: with no `SMTP_HOST` there is nothing to turn on, and an operator who configured
     * mail deliberately should not have to find a second switch. What this buys is the other
     * direction — a phishing campaign, or a mail server somebody else now controls, is answered from
     * the settings screen in seconds rather than by an edit to `.env` and a restart.
     *
     * There is no coherence rule pairing it with the SMTP variables, and there cannot be: coherence is
     * computed over settings alone and cannot see the environment. The effective predicate is
     * `SMTP_HOST configured AND this setting`, and it lives in the api's composition root.
     */
    'security.self_service_reset': { schema: z.boolean(), defaultValue: true, secret: false },
    'security.pin_attempts_before_password': {
        schema: z.int().min(1).max(20),
        defaultValue: 5,
        secret: false,
    },
    'alerts.record_views_per_operator_per_day': {
        schema: z.int().min(1).max(100_000),
        defaultValue: 150,
        secret: false,
    },
    'alerts.records_per_api_key_per_hour': {
        schema: z.int().min(1).max(1_000_000),
        defaultValue: 1000,
        secret: false,
    },
    /**
     * Who is mailed when AD-37's exfiltration alert is RAISED. Empty means nobody, and that is the
     * shipped state.
     *
     * AD-37 says a crossed threshold raises a dashboard alert "and optionally an email", and until
     * this key existed there was nowhere to send one: Section 1.8 declared recipient lists for the
     * nightly archive and for the expiry digest and none for security findings, so the bilingual
     * `exfiltrationAlert` template existed with no consumer and the only alternative would have been
     * to mail a security finding to whoever receives backup notices.
     *
     * **Secret, exactly as the other two recipient lists are.** A list of the addresses that receive
     * this installation's security alerts is a list of the people worth phishing first.
     *
     * **There is deliberately no coherence rule pairing it with a threshold**, and the reason is that
     * the pairing the other two lists have does not exist here. `backup.recipients` is required when
     * `backup.schedule_enabled` is true and `expiry_alerts.recipients` when `expiry_alerts.enabled`
     * is true, because in both cases the feature is off until an administrator turns it on. The
     * exfiltration control has no such flag: both thresholds are plain integers with shipped
     * defaults, so a threshold is ALWAYS configured, and a rule of the form "a configured threshold
     * with no recipients is refused" would make a fresh installation's settings incoherent on the day
     * it boots and would refuse every settings write until somebody typed an address — for a delivery
     * AD-37 calls optional. The empty list IS the opt-out, and the handler mails on a raise only,
     * never on a refresh, or an hourly evaluation of a standing breach becomes an hourly email.
     */
    'alerts.exfiltration_recipients': { schema: recipientsSchema, defaultValue: [], secret: true },
    'storage.max_concurrent_uploads': {
        schema: z.int().min(1).max(64),
        defaultValue: 4,
        secret: false,
    },
    'persons.warn_if_missing': {
        schema: z.array(completenessKeySchema).max(50),
        defaultValue: [
            'phone_1',
            'date_of_birth',
            'country_of_origin',
            'passport_number',
            IMAGES_COMPLETENESS_KEY,
        ],
        secret: false,
    },
    'backup.schedule_enabled': { schema: z.boolean(), defaultValue: false, secret: false },
    /**
     * The nightly archive, in `REPORTING_TIMEZONE` like every other schedule (AD-45).
     *
     * **It is the only schedule derived from a stored setting, and it is therefore the one AD-45's
     * daylight-saving proof is written against.** `jobs/__tests__/registry.test.ts` expands this cron
     * across both European transitions and asserts it fires at three in the morning local time on
     * either side of each. That proof used to be written against the nightly auto-checkout's own
     * time; when that job was retired the proof was rebased here rather than deleted, because the
     * property it pins — that a stored `HH:MM` becomes a cron interpreted in the installation's zone
     * — belongs to whichever schedule is derived, not to any particular job.
     *
     * `retention.purge` and `exports.cleanup` sit at `20 3` and `40 3`; an installation that enables
     * the nightly archive should keep it clear of them, because all three read the whole database and
     * the host has one volume.
     */
    'backup.cron': { schema: cronSchema, defaultValue: '0 3 * * *', secret: false },
    'backup.recipients': { schema: recipientsSchema, defaultValue: [], secret: true },
    'expiry_alerts.enabled': { schema: z.boolean(), defaultValue: false, secret: false },
    'expiry_alerts.days_before': {
        schema: z.array(z.int().min(0).max(3650)).max(10),
        defaultValue: [90, 60, 30, 0],
        secret: false,
    },
    'expiry_alerts.recipients': { schema: recipientsSchema, defaultValue: [], secret: true },
    'retention.persons_enabled': { schema: z.boolean(), defaultValue: false, secret: false },
    'retention.persons_inactive_days': {
        schema: z.int().min(30).max(36_500).nullable(),
        defaultValue: null,
        secret: false,
    },
    /**
     * `0`, meaning keep forever, and it is the only value this installation can represent.
     *
     * **`z.literal(0)` rather than a range, for the same reason
     * {@link SETTINGS_REGISTRY}['presence.require_override_reason'] is `z.literal(true)`**: the value
     * a range would admit is unrepresentable in the engine, so admitting it would store a
     * configuration that silently does nothing. Three independent layers refuse an audit purge, and
     * each of them refuses it for everybody:
     *
     * 1. The application role has `UPDATE, DELETE, TRUNCATE` revoked on `audit_log`.
     * 2. An `audit_log_append_only` trigger raises on `UPDATE` or `DELETE` for every role, the schema
     *    owner included, so a future re-grant or a migration cannot quietly lift it either.
     * 3. Even a successful delete would be worse than a refused one. Purging the OLDEST rows is a head
     *    truncation, and chain verification anchors the first row of its range on the genesis constant
     *    and asserts contiguity from `seq` 1 — so every later run would report both a broken chain and
     *    a sequence gap, permanently, and the chain-break alert raised on the strength of it could
     *    never be cleared.
     *
     * The key is kept rather than deleted, so the documented guarantee still reads as a guarantee and
     * the seeded row still exists. What is refused is the value that cannot be honoured, at the one
     * choke point every write passes through, which makes the impossible state unreachable rather
     * than merely unhandled by whichever reader happens to look at it. Two independent reviewers
     * weighed the alternatives separately and both reached this verdict.
     *
     * **If audit retention is ever genuinely required**, it is not a settings change: it needs the
     * trigger dropped by migration, `DELETE` re-granted, a signed low-water mark stored somewhere, and
     * the verifier taught to anchor a range that does not start at 1 — and that last part is the
     * disqualifier, because an anchor accepted at an arbitrary `seq` makes an operator's purge and an
     * attacker's head deletion indistinguishable.
     */
    'retention.audit_days': { schema: z.literal(0), defaultValue: 0, secret: false },
    'retention.exports_days': { schema: z.int().min(1).max(365), defaultValue: 7, secret: false },
    'retention.api_usage_days': { schema: z.int().min(1).max(3650), defaultValue: 90, secret: false },
    'retention.scan_attempts_days': {
        schema: z.int().min(1).max(3650),
        defaultValue: 365,
        secret: false,
    },
    'consent.required': { schema: z.boolean(), defaultValue: false, secret: false },
    // **Three, because two releases have changed what is collected.** `docs/compliance/data-map.md`
    // states the rule this follows: a change to the fields collected is a change to the notice, so it
    // raises the version. Nothing seeds the settings table — a key with no row IS its default — so an
    // installation that never set this key moves with the product, which is the intended effect and is
    // written down in the data map beside the register of what each version covers. The intake release
    // took it to 2; the reference code takes it to 3. What changed is the FIELD SET rather than any
    // notice text in this repository, and the register says so rather than implying a revision.
    'consent.notice_version': { schema: z.int().min(1).max(9999), defaultValue: 3, secret: false },
    /**
     * AD-59: how few unclaimed pre-printed bands are left before somebody is told.
     *
     * Evaluated at boot and then on each claim, each import and each void, rather than on a
     * schedule: one count on a path that already holds the row, no queue entry and therefore no
     * timezone to get wrong.
     */
    'passes.low_blank_threshold': {
        schema: z.int().min(0).max(100_000),
        defaultValue: 100,
        secret: false,
    },
    /**
     * What a registration carrying no band produces, in `preprinted` mode.
     *
     * A live setting rather than an environment variable like the mode itself, because a control that
     * needs a restart is not a break-glass control.
     *
     * **Three values rather than two booleans, so the incoherent state is unrepresentable.** Its
     * predecessor was `passes.allow_emergency_mint`, and the day a second answer was needed — the
     * supplier delayed a shipment and people still had to be taken in — a second boolean beside it
     * would have described one condition with two switches and different outcomes, needing a
     * coherence rule to forbid both and a precedence order for the state that rule could not see. One
     * key with three answers needs neither.
     *
     * - `refuse`: the band is required and the whole registration is rolled back without it
     *   (`CREDENTIAL_CODE_REQUIRED`). The default, and the only value that keeps the guard whole.
     * - `defer`: the person is registered with NO pass, and the band is bound later from their own
     *   record. The registration must then carry a reference code instead, so nobody walks away
     *   unidentifiable — that is `PERSON_REFERENCE_REQUIRED`, and it is what makes this a replacement
     *   of the guard rather than a removal of it.
     * - `emergency`: a pass is minted and printed on paper. The abuse it permits stays fully visible,
     *   because every such credential carries `origin = 'emergency_mint'` forever and is counted on
     *   the dashboard.
     *
     * **The value is `emergency` and the origin is `emergency_mint`, deliberately.** They are two
     * different questions — what to do when no band is offered, and how a credential came to exist —
     * and sharing one token made the browser's issuance-literal census unable to tell a setting value
     * from a credential origin without an allow-list entry, which would have been a control weakened
     * to accommodate a name.
     */
    'passes.no_band_at_registration': {
        schema: noBandAtRegistrationSchema,
        defaultValue: 'refuse',
        secret: false,
    },
    /**
     * Whether this installation controls meals at all. Ships OFF.
     *
     * **The default is what makes the release inert on arrival.** Nothing seeds the settings table, so
     * a key with no row IS its default: an installation that pulls this update gets the migration, the
     * routes and the screens, and an operator sees nothing change until a lead administrator turns
     * this on. That is deliberate for a product already running in a facility with real residents in
     * it, and it is the reason the switch exists rather than the feature simply shipping.
     *
     * It reaches the browser through `GET /api/v1/config` as `diningEnabled`, because the
     * administration dashboard has to know whether to offer the panel and an administrator cannot
     * read the settings route. When it is off the scan's `dining` block is `null`, every dining route
     * answers `SYSTEM_FEATURE_DISABLED`, and the dashboard tab is not drawn: a hidden control and a
     * closed door are one decision rather than two.
     */
    'dining.enabled': { schema: z.boolean(), defaultValue: false, secret: false },
    /**
     * The hour lunch service begins and the hour dinner service begins, in the reporting timezone.
     *
     * **One key with two ordered values rather than two keys, for the reason
     * {@link SETTINGS_REGISTRY}['passes.no_band_at_registration'] is one key with three values.** The
     * incoherent state here is "dinner starts before lunch", which suggests lunch at no hour of any
     * day and silently removes a suggestion the counter is drawn around. As two independent integers
     * that needs a coherence rule, a message key and a second place to look; as one ordered pair it is
     * unrepresentable, refused at the write by the schema itself, and the refusal names the key.
     *
     * What it decides is only which button is EMPHASISED. A meal served at any hour is recorded
     * normally: a counter that refused breakfast at eleven would be a counter that cannot feed
     * somebody who overslept, and the allowance is the control here, not the clock. Before the first
     * hour the suggestion is breakfast, which is also the honest answer at three in the morning.
     *
     * It reuses the array-of-integers control `expiry_alerts.days_before` already has, so the
     * administration screen needs no new kind of input.
     */
    'dining.meal_start_hours': {
        schema: z
            .array(z.int().min(0).max(23))
            .length(2)
            .refine((hours) => (hours[0] ?? 0) < (hours[1] ?? 0), {
            error: 'errors.validation.invalid_option',
        }),
        defaultValue: [11, 17],
        secret: false,
    },
    'ui.default_theme': {
        schema: z.enum(['system', 'light', 'dark']),
        defaultValue: 'system',
        secret: false,
    },
};
export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY);
export const settingKeySchema = z.enum(SETTING_KEYS);
/** Whether a string names a real setting. */
export function isSettingKey(value) {
    return Object.hasOwn(SETTINGS_REGISTRY, value);
}
/** Whether a setting's value is stored encrypted and withheld from a non-administrator. */
export function isSecretSetting(key) {
    return SETTINGS_REGISTRY[key].secret;
}
/** Validates one value against the schema its key declares. */
export function parseSetting(key, value) {
    return SETTINGS_REGISTRY[key].schema.safeParse(value);
}
/** The shipped defaults, as a plain object, for seeding and for a `db` test to compare against. */
export function defaultSettings() {
    const defaults = {};
    for (const key of SETTING_KEYS) {
        defaults[key] = SETTINGS_REGISTRY[key].defaultValue;
    }
    return defaults;
}
/** Writing one setting. The value is validated against the key's own schema, not against `unknown`. */
export const updateSettingRequestSchema = strict({
    key: settingKeySchema,
    value: z.unknown(),
});
/**
 * The body of `PUT /api/v1/settings/:key`: the value, and nothing else.
 *
 * Separate from {@link updateSettingRequestSchema} because the key travels in the PATH there, and
 * it lives in this package rather than beside the route for the reason every other wire shape does:
 * the browser's route table parses and builds requests from the contracts package alone, so a body
 * declared only in the server is a body the client has to restate — and a restatement is a second
 * opinion about a shape whose whole job is to have one.
 */
export const settingValueRequestSchema = strict({ value: z.unknown() });
/**
 * The rules that involve more than one setting.
 *
 * Each of these is a way to leave the installation in a state that looks configured and silently
 * does nothing: a schedule with nobody to mail, or a retention job enabled with no period, which
 * would either delete nothing or, worse, be given a period later by somebody who did not realise
 * the job was already on.
 */
export function checkSettingsCoherence(values) {
    // `MessageKey` rather than `string`: a typo here used to fall through a `string`-to-key fallback
    // in the reader and render as "the format is not valid" on a settings screen, which is the
    // sentence this product is trying to stop saying. Now it does not compile.
    const problems = [];
    const recipients = values['backup.recipients'];
    if (values['backup.schedule_enabled'] === true &&
        (!Array.isArray(recipients) || recipients.length === 0)) {
        problems.push({
            key: 'backup.recipients',
            messageKey: 'errors.backup.recipients_required',
        });
    }
    const alertRecipients = values['expiry_alerts.recipients'];
    if (values['expiry_alerts.enabled'] === true &&
        (!Array.isArray(alertRecipients) || alertRecipients.length === 0)) {
        problems.push({
            key: 'expiry_alerts.recipients',
            messageKey: 'errors.backup.recipients_required',
        });
    }
    if (values['retention.persons_enabled'] === true &&
        (values['retention.persons_inactive_days'] ?? null) === null) {
        problems.push({
            key: 'retention.persons_inactive_days',
            messageKey: 'errors.validation.required',
        });
    }
    return problems;
}
/** The core field keys the shipped completeness warning names, for a test to compare against. */
export const SHIPPED_COMPLETENESS_KEYS = [
    ...CORE_FIELD_KEYS.filter((key) => ['phone_1', 'date_of_birth', 'country_of_origin', 'passport_number'].includes(key)),
    IMAGES_COMPLETENESS_KEY,
];
/**
 * One setting as the administration panel sees it, and the reason a secret one looks different.
 *
 * **A secret setting's `value` is always `null` on the wire, whether or not one is stored.** It is
 * write-only from the API's point of view: it can be set, and its PRESENCE can be reported, and it
 * can never be read back. That is not a permission check that could be widened later — the route
 * has no branch that returns the value at all — because a recipient list is exactly the kind of
 * thing that ends up in a screenshot, a support ticket and a browser cache, and an administrator
 * who needs to know what it says can replace it.
 *
 * `configured` is what makes the write-only value usable: it is `true` when a row exists and holds
 * something non-empty, so the panel can render "3 recipients configured" and a coherence failure
 * can be explained without disclosing anything.
 *
 * `isDefault` says the installation has never written this key, so the shipped default is what is
 * in force. It is distinct from `configured`: a non-secret setting written to the same value the
 * registry ships is configured and not default, and a reader has to be able to tell the two apart
 * to answer "has anybody touched this installation's session windows".
 */
export const settingViewSchema = strict({
    key: settingKeySchema,
    secret: z.boolean(),
    /** The value in force, or `null` for a secret one. See the note above. */
    value: z.unknown(),
    configured: z.boolean(),
    isDefault: z.boolean(),
    updatedAt: isoDateTimeSchema.nullable(),
    updatedBy: uuidSchema.nullable(),
});
/** One coherence problem, as a key and a catalogue message key. Never a value. */
export const settingsProblemSchema = strict({
    key: settingKeySchema,
    messageKey: z.string().min(1).max(120),
});
/**
 * The whole registry, in one response, plus whatever is currently incoherent.
 *
 * The problems travel with the list rather than only on a write, because an installation can
 * become incoherent without anybody writing a setting: `checkSettingsCoherence` reads several keys
 * at once, and the panel has to be able to show the state it is in when it opens.
 */
export const settingsViewSchema = strict({
    items: z.array(settingViewSchema),
    problems: z.array(settingsProblemSchema),
});
//# sourceMappingURL=settings.js.map